import React, { useEffect } from 'react';
import { X } from 'lucide-react';
interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function TermsModal({
  isOpen,
  onClose
}: TermsModalProps) {
  // Lock body scroll while open + close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="terms-title" data-id="element-286">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-287" />

      {/* Modal */}
      <div className="relative bg-card border border-gold/30 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" data-id="element-288">
        {/* Header */}
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-border bg-secondary" data-id="element-289">
          <div data-id="element-290">
            <h2 id="terms-title" className="font-serif text-xl sm:text-2xl text-white" data-id="element-291">
              Términos y Condiciones
            </h2>
            <p className="text-gray text-xs mt-1" data-id="element-292">
              Odontología Estética Pereira · Última actualización: Junio 2026
            </p>
          </div>
          <button onClick={onClose} className="text-gray hover:text-white p-2 rounded-lg hover:bg-primary transition-colors" aria-label="Cerrar" data-id="element-293">
            <X size={22} data-id="element-294" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 sm:px-8 py-6 text-sm leading-relaxed text-white/85 space-y-8" data-id="element-295">
          <Section title="1. Información General" data-id="element-296">
            <p data-id="element-297">
              Odontología Estética es una clínica odontológica ubicada en Cra. 6
              #21-37 Local 1, Pereira, Risaralda 660001, Colombia. Al utilizar
              este sitio web o nuestros servicios, usted acepta los presentes
              términos y condiciones en su totalidad.
            </p>
          </Section>

          <Section title="2. Servicios Profesionales" data-id="element-298">
            <p data-id="element-299">
              Todos los tratamientos odontológicos son ejecutados por
              profesionales debidamente certificados y habilitados por las
              autoridades sanitarias colombianas. Los resultados de cualquier
              tratamiento estético dependen de las condiciones individuales de
              cada paciente, por lo que pueden variar y no son garantizados de
              manera absoluta.
            </p>
            <p data-id="element-300">
              La{' '}
              <span className="text-gold font-semibold" data-id="element-301">
                valoración inicial
              </span>{' '}
              es completamente gratuita y no compromete al paciente a la
              contratación de ningún servicio.
            </p>
          </Section>

          <Section title="3. Precios y Formas de Pago" data-id="element-302">
            <p data-id="element-303">
              Los precios publicados son referenciales en pesos colombianos
              (COP) y pueden variar según el diagnóstico individual del
              paciente. El presupuesto definitivo se entregará por escrito al
              finalizar la valoración. Aceptamos pagos en efectivo,
              transferencia bancaria, tarjetas débito/crédito y planes de
              financiación disponibles.
            </p>
          </Section>

          <Section title="4. Política de Citas y Cancelaciones" data-id="element-304">
            <p data-id="element-305">
              Las citas pueden agendarse vía WhatsApp al{' '}
              <span className="text-gold font-semibold" data-id="element-306">301 589 2967</span> o a
              través del formulario web. Solicitamos un aviso con al menos 24
              horas de anticipación para la cancelación o reprogramación de
              citas. Las inasistencias reiteradas sin previo aviso pueden
              afectar la disponibilidad de nuevas citas.
            </p>
          </Section>

          <Section title="5. Tratamiento de Datos Personales (Ley 1581 de 2012)" data-id="element-307">
            <p data-id="element-308">
              En cumplimiento de la Ley 1581 de 2012 y el Decreto 1377 de 2013
              de la República de Colombia, Odontología Estética informa que los
              datos personales recolectados a través de este sitio (nombre,
              teléfono, correo electrónico, mensaje) serán utilizados
              exclusivamente para:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2" data-id="element-309">
              <li data-id="element-310">Gestionar la solicitud de valoración o información.</li>
              <li data-id="element-311">Contactar al usuario para confirmar o coordinar citas.</li>
              <li data-id="element-312">
                Enviar información comercial relacionada con nuestros servicios.
              </li>
              <li data-id="element-313">
                Llevar la historia clínica en caso de iniciar tratamiento.
              </li>
            </ul>
            <p className="mt-3" data-id="element-314">
              El titular de los datos podrá en cualquier momento solicitar
              acceso, corrección, actualización o supresión de su información
              escribiendo a{' '}
              <a href="mailto:odontoesteticapereira@gmail.com" className="text-gold hover:underline" data-id="element-315">
                odontoesteticapereira@gmail.com
              </a>
              .
            </p>
          </Section>

          <Section title="6. Propiedad Intelectual" data-id="element-316">
            <p data-id="element-317">
              Todos los contenidos del sitio (textos, imágenes, fotografías de
              antes/después, logotipos, diseño gráfico) son propiedad de
              Odontología Estética Pereira o se utilizan bajo autorización
              expresa de sus titulares. Las imágenes de pacientes publicadas en
              este sitio cuentan con consentimiento escrito previo. Queda
              prohibida su reproducción total o parcial sin autorización previa.
            </p>
          </Section>

          <Section title="7. Consentimiento Fotográfico" data-id="element-318">
            <p data-id="element-319">
              Cuando un paciente decide compartir resultados en redes sociales o
              en este sitio web, firma un consentimiento informado por separado.
              Si usted reconoce alguna imagen y desea solicitar su retiro,
              escríbanos a{' '}
              <a href="mailto:odontoesteticapereira@gmail.com" className="text-gold hover:underline" data-id="element-320">
                odontoesteticapereira@gmail.com
              </a>{' '}
              y procederemos en un plazo no mayor a 15 días hábiles.
            </p>
          </Section>

          <Section title="8. Limitación de Responsabilidad" data-id="element-321">
            <p data-id="element-322">
              La información presentada en este sitio web tiene fines
              informativos. No reemplaza una consulta odontológica presencial.
              Odontología Estética no se hace responsable por decisiones tomadas
              con base únicamente en el contenido del sitio, sin haber recibido
              una valoración profesional previa.
            </p>
          </Section>

          <Section title="9. Modificaciones" data-id="element-323">
            <p data-id="element-324">
              Odontología Estética se reserva el derecho de modificar estos
              términos en cualquier momento. Los cambios entrarán en vigencia al
              ser publicados en el sitio web. Recomendamos revisar
              periódicamente esta sección.
            </p>
          </Section>

          <Section title="10. Contacto" data-id="element-325">
            <p data-id="element-326">Para cualquier consulta sobre los presentes términos:</p>
            <ul className="list-none space-y-1 mt-2" data-id="element-327">
              <li data-id="element-328">📍 Cra. 6 #21-37 Local 1, Pereira, Risaralda 660001</li>
              <li data-id="element-329">📱 WhatsApp: 301 589 2967</li>
              <li data-id="element-330">📧 odontoesteticapereira@gmail.com</li>
            </ul>
          </Section>
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 sm:px-8 py-4 bg-secondary flex justify-end" data-id="element-331">
          <button onClick={onClose} className="bg-gold hover:bg-gold-hover text-primary font-semibold px-6 py-2.5 rounded-lg transition-colors" data-id="element-332">
            Entendido
          </button>
        </div>
      </div>
    </div>;
}
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section data-id="element-333">
      <h3 className="font-serif text-lg text-gold mb-3" data-id="element-334">{title}</h3>
      <div className="space-y-2" data-id="element-335">{children}</div>
    </section>;
}